import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import KernelWidget from "../KernelWidget.jsx";
import HoverTooltip from "../HoverTooltip.jsx";
import CanvasRuntimeControls, {
    CanvasDisclosures,
    CanvasRuntimeNotice,
} from "../CanvasRuntimeControls.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import {
    rowGridColumns,
    enforceSoloRows,
    runtimeRowCellStyle,
} from "../../lib/canvasLayout.js";
import {
    applyCanvasCellProjection,
    buildCanvasCellProjection,
    canvasRuntimeStateIsDefault,
    clearCanvasRuntimeState,
    compileCanvasRuntime,
    defaultCanvasRuntimeState,
    groupCanvasRuntimeRows,
    isCanvasRuntimeScalar,
    readCanvasRuntimeState,
    writeCanvasRuntimeState,
} from "../../lib/canvasRuntime.js";
import {
    canvasCellKernelId,
    resolveCanvasCellKernel,
} from "../../lib/kernelResolver.js";
import {
    CanvasRunScheduler,
    DEFAULT_CANVAS_RUN_CONCURRENCY,
    DEFAULT_KERNEL_RUN_TIMEOUT_MS,
} from "../../lib/canvasRunScheduler.js";
import {
    KERNEL_RUN_CANCEL_REASON,
    KERNEL_RUN_OUTCOME,
} from "../../lib/kernelRunOutcome.js";
import { normalizePythonPackageRequirements } from "../../lib/pythonPackageRequirements.js";

function pendingRunKey(cellKey, event) {
    return JSON.stringify([cellKey, event?.runId ?? null]);
}

// Shared canvas renderer: maps a canvas's normalized rows to a grid of
// KernelWidget cards. Used by BOTH the Canvases scope lens (CanvasesBody), the
// standalone Canvases home page (CanvasHomePage), and the detail page's view
// mode so they render identically and can't drift.
//
// Layout: an outer column of rows. Existing canvases use the rowGridColumns
// grid unchanged. An opted-in runtime uses wrapping flex rows with a generic
// readability floor that a runtime cell can override.
//
// Chrome: one card frame per cell (the <article>), a single caption/title line,
// and the slug + refresh control revealed on hover, so a viewing dashboard
// reads clean while the builder affordances stay a hover away.
export default function CanvasItems({
    rows,
    kernelResolver,
    legacyCellAdapter = null,
    runContext = null,
    kernelParams,
    onOpenScope,
    version = 0,
    emptyNote,
    runtime = null,
    canvasKey = null,
    tenantId = null,
    runConcurrency = DEFAULT_CANVAS_RUN_CONCURRENCY,
    runTimeoutMs = DEFAULT_KERNEL_RUN_TIMEOUT_MS,
    onRunOutcome = null,
}) {
    // Apply the solo-kind rule here, where the shared resolver provides render kinds:
    // a treemap never shares a row, no matter how the canvas was authored.
    const laidOutRows = useMemo(
        () => enforceSoloRows(
            rows,
            (_command, cell) => resolveCanvasCellKernel(
                cell,
                kernelResolver,
                { legacyAdapter: legacyCellAdapter },
            )?.body?.render?.kind,
        ),
        [rows, kernelResolver, legacyCellAdapter],
    );
    const compiledRuntime = useMemo(
        () => compileCanvasRuntime(runtime, laidOutRows),
        [runtime, laidOutRows],
    );
    const runtimeContextKey = JSON.stringify([
        tenantId,
        canvasKey,
        compiledRuntime.signature,
    ]);
    return (
        <CanvasItemsBody
            key={runtimeContextKey}
            laidOutRows={laidOutRows}
            compiledRuntime={compiledRuntime}
            kernelResolver={kernelResolver}
            legacyCellAdapter={legacyCellAdapter}
            runContext={runContext}
            kernelParams={kernelParams}
            onOpenScope={onOpenScope}
            version={version}
            emptyNote={emptyNote}
            canvasKey={canvasKey}
            tenantId={tenantId}
            runConcurrency={runConcurrency}
            runTimeoutMs={runTimeoutMs}
            onRunOutcome={onRunOutcome}
        />
    );
}

function CanvasItemsBody({
    laidOutRows,
    compiledRuntime,
    kernelResolver,
    legacyCellAdapter,
    runContext,
    kernelParams,
    onOpenScope,
    version,
    emptyNote,
    canvasKey,
    tenantId,
    runConcurrency,
    runTimeoutMs,
    onRunOutcome,
}) {
    const [runtimeState, setRuntimeState] = useState(
        () => readCanvasRuntimeState(tenantId, canvasKey, compiledRuntime),
    );
    const [focus, setFocus] = useState(null);
    const [runtimeRefresh, setRuntimeRefresh] = useState(0);
    useEffect(() => {
        writeCanvasRuntimeState(tenantId, canvasKey, compiledRuntime, runtimeState);
    }, [canvasKey, compiledRuntime, runtimeState, tenantId]);
    const scheduler = useMemo(
        () => new CanvasRunScheduler({
            concurrency: runConcurrency,
            timeoutMs: runTimeoutMs,
        }),
        [runConcurrency, runTimeoutMs],
    );
    // Signature-then-parse, not a plain array memo: `laidOutRows` is a fresh
    // array on every host render (normalizeRows allocates), so an array keyed
    // on it would hand KernelWidget a new reference each time. That reference
    // is part of the widget's run identity, so every card on the canvas would
    // re-run and blank out on any host re-render. Only the content matters.
    const executionPackageSignature = useMemo(() => {
        const packages = new Set();
        for (const row of laidOutRows) {
            for (const cell of row.cells) {
                const resolved = resolveCanvasCellKernel(
                    cell,
                    kernelResolver,
                    { legacyAdapter: legacyCellAdapter },
                );
                for (const step of resolved?.body?.steps || []) {
                    if (step?.kind !== "python") continue;
                    try {
                        for (const packageName of normalizePythonPackageRequirements(
                            step.packages,
                        )) {
                            packages.add(packageName);
                        }
                    } catch {
                        // The kernel runner reports the invalid declaration on
                        // that card; it must not prevent independent cells from
                        // entering the canvas batch.
                    }
                }
            }
        }
        return JSON.stringify([...packages].sort());
    }, [kernelResolver, laidOutRows, legacyCellAdapter]);
    const executionPackageRequirements = useMemo(
        () => JSON.parse(executionPackageSignature),
        [executionPackageSignature],
    );
    const [pendingRunKeys, setPendingRunKeys] = useState(() => new Set());
    const onRunOutcomeRef = useRef(onRunOutcome);
    const schedulerRef = useRef(scheduler);
    const unmountingRef = useRef(false);
    onRunOutcomeRef.current = onRunOutcome;
    schedulerRef.current = scheduler;

    useEffect(() => {
        unmountingRef.current = false;
        return () => {
            unmountingRef.current = true;
        };
    }, []);

    useEffect(() => () => {
        queueMicrotask(() => {
            // StrictMode replays effects with the same scheduler. Dispose only
            // when the canvas really closes or configuration replaces it.
            if (unmountingRef.current || schedulerRef.current !== scheduler) {
                scheduler.dispose({ code: KERNEL_RUN_CANCEL_REASON.SCHEDULER_DISPOSED });
            }
        });
    }, [scheduler]);

    const handleCellRunState = useCallback((cellKey, event) => {
        setPendingRunKeys((current) => {
            const next = new Set(current);
            const key = pendingRunKey(cellKey, event);
            if (["queued", "running", "cancelling"].includes(event.state)) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });
    }, []);

    const handleCellRunOutcome = useCallback((cellKey, cell, event) => {
        setPendingRunKeys((current) => {
            const key = pendingRunKey(cellKey, event);
            if (!current.has(key)) return current;
            const next = new Set(current);
            next.delete(key);
            return next;
        });
        onRunOutcomeRef.current?.({
            ...event,
            cellKey,
            cell,
        });
    }, []);

    const handleControlChange = useCallback((controlId, value) => {
        setRuntimeState((current) => ({
            ...current,
            controls: { ...current.controls, [controlId]: value },
        }));
    }, []);
    const handleFocus = useCallback((sourceCellId, fieldOrFocus, value) => {
        const field = typeof fieldOrFocus === "object" ? fieldOrFocus?.field : fieldOrFocus;
        const nextValue = typeof fieldOrFocus === "object" ? fieldOrFocus?.value : value;
        const focusField = typeof field === "string" ? field.trim() : "";
        const sourceCell = compiledRuntime.cellsById.get(sourceCellId);
        const supported = sourceCell?.focus.some((binding) => binding.field === focusField);
        setFocus(supported && focusField.length <= 128 && isCanvasRuntimeScalar(nextValue)
            ? { field: focusField, value: nextValue, sourceCellId }
            : null);
    }, [compiledRuntime]);
    const resetRuntime = useCallback(() => {
        clearCanvasRuntimeState(tenantId, canvasKey);
        setRuntimeState(defaultCanvasRuntimeState(compiledRuntime));
        setFocus(null);
    }, [canvasKey, compiledRuntime, tenantId]);
    const stopAll = useCallback(() => scheduler.cancelAll({
        code: KERNEL_RUN_CANCEL_REASON.USER_STOP,
    }), [scheduler]);
    const groups = useMemo(
        () => groupCanvasRuntimeRows(laidOutRows, compiledRuntime),
        [compiledRuntime, laidOutRows],
    );

    const renderRow = (row, rowKey) => {
        const runtimeEnabled = compiledRuntime.enabled && compiledRuntime.status === "ready";
        const rowIndex = laidOutRows.indexOf(row);
        return (
            <div
                key={rowKey}
                className={runtimeEnabled
                    ? "flex flex-wrap items-stretch gap-[10px] min-w-0"
                    : "grid gap-[10px] min-w-0"}
                style={runtimeEnabled ? undefined : { gridTemplateColumns: rowGridColumns(row) }}
            >
                {row.cells.map((cell, cellIndex) => {
                    const runtimeCell = runtimeEnabled
                        ? compiledRuntime.cellsByKernelId.get(cell.kernel_id)
                        : null;
                    const projection = runtimeCell
                        ? buildCanvasCellProjection(
                            compiledRuntime,
                            runtimeCell,
                            runtimeState.controls,
                            focus,
                        )
                        : null;
                    const resolvedKernel = resolveCanvasCellKernel(
                        cell,
                        kernelResolver,
                        { legacyAdapter: legacyCellAdapter },
                    );
                    const identity = runtimeCell?.id
                        || canvasCellKernelId(cell)
                        || cell.command
                        || "missing";
                    const cellKey = runtimeCell?.id || `${identity}-${rowIndex}-${cellIndex}`;
                    return (
                        <CanvasCard
                            key={cellKey}
                            cellKey={cellKey}
                            cell={cell}
                            style={runtimeEnabled
                                ? runtimeRowCellStyle(cell, runtimeCell)
                                : null}
                            resolvedKernel={resolvedKernel}
                            runtimeCell={runtimeCell}
                            projection={projection}
                            runContext={runContext}
                            runScheduler={scheduler}
                            executionPackageRequirements={executionPackageRequirements}
                            runTimeoutMs={scheduler.timeoutMs}
                            onRunStateChange={handleCellRunState}
                            onRunOutcome={handleCellRunOutcome}
                            kernelParams={kernelParams}
                            onOpenScope={onOpenScope}
                            onFocus={handleFocus}
                            version={version + runtimeRefresh}
                        />
                    );
                })}
            </div>
        );
    };

    if (!laidOutRows.length) {
        return (
            <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[12px]">
                <strong className="text-[13px] leading-[1.08] font-medium">This canvas has no widgets</strong>
                <span className={NOTE_CLASS}>{emptyNote || "Add kernels to it from the canvas editor."}</span>
            </div>
        );
    }
    const pendingCount = pendingRunKeys.size;
    const runtimeReady = compiledRuntime.enabled && compiledRuntime.status === "ready";
    const resetVisible = runtimeReady
        && !canvasRuntimeStateIsDefault(compiledRuntime, runtimeState, focus);
    return (
        <div
            className="flex flex-col gap-[10px] min-w-0 [overflow-wrap:anywhere]"
            aria-busy={pendingCount ? "true" : undefined}
        >
            <CanvasDisclosures
                disclosures={compiledRuntime.status === "ready"
                    ? compiledRuntime.disclosures
                    : []}
            />
            <CanvasRuntimeControls
                compiled={compiledRuntime}
                state={runtimeState}
                focus={focus}
                pendingCount={pendingCount}
                resetVisible={resetVisible}
                onControlChange={handleControlChange}
                onClearFocus={() => setFocus(null)}
                onRefresh={() => setRuntimeRefresh((current) => current + 1)}
                onStop={stopAll}
                onReset={resetRuntime}
            />
            {!compiledRuntime.enabled && pendingCount ? (
                <div
                    data-export-ignore="true"
                    className="flex items-center justify-end gap-[8px] text-[10px] text-[color:var(--muted-soft)]"
                    aria-live="polite"
                >
                    <span>{pendingCount} kernel run{pendingCount === 1 ? "" : "s"} pending</span>
                    <button
                        type="button"
                        onClick={stopAll}
                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[7px] py-[1px] text-[9px] uppercase tracking-[0.14em] hover:border-[color:var(--red,#c0392b)] hover:text-[color:var(--red,#c0392b)]"
                    >
                        stop all
                    </button>
                </div>
            ) : null}
            {groups.map((group, groupIndex) => {
                const band = group.band;
                const open = !band || Boolean(runtimeState.openBands[band.id]);
                return (
                    <div key={group.id} className="flex min-w-0 flex-col gap-[10px]">
                        {band ? (
                            <button
                                type="button"
                                data-export-ignore="true"
                                aria-expanded={open}
                                onClick={() => setRuntimeState((current) => ({
                                    ...current,
                                    openBands: {
                                        ...current.openBands,
                                        [band.id]: !open,
                                    },
                                }))}
                                className="flex w-full items-baseline gap-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[9px] py-[7px] text-left"
                            >
                                <span className="font-mono text-[10px] text-[color:var(--muted-soft)]">
                                    {open ? "-" : "+"}
                                </span>
                                <span className="text-[11px] font-medium text-[color:var(--ink)]">
                                    {band.title}
                                </span>
                                {band.description ? (
                                    <span className="min-w-0 flex-1 truncate text-[10px] text-[color:var(--muted)]">
                                        {band.description}
                                    </span>
                                ) : <span className="flex-1" />}
                                <span className="font-mono text-[9px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">
                                    {open ? "hide" : `${group.rows.length} row${group.rows.length === 1 ? "" : "s"}`}
                                </span>
                            </button>
                        ) : null}
                        {open ? group.rows.map((row, rowIndex) => (
                            renderRow(row, `${groupIndex}-${rowIndex}`)
                        )) : null}
                    </div>
                );
            })}
        </div>
    );
}

function CanvasCard({
    cellKey,
    cell,
    resolvedKernel,
    runtimeCell,
    projection,
    style,
    runContext,
    runScheduler,
    executionPackageRequirements,
    runTimeoutMs,
    onRunStateChange,
    onRunOutcome,
    kernelParams,
    onOpenScope,
    onFocus,
    version = 0,
}) {
    // Per-card refresh nonce, summed with the parent's `version` (the page-wide
    // "Refresh all"). Both only increment, so the sum strictly increases and
    // every bump re-runs the kernel.
    const [{ bump, explicitRunToken }, setRefreshState] = useState({
        bump: 0,
        explicitRunToken: null,
    });
    const kernel = resolvedKernel?.body;
    const reference = cell.command ? `/${cell.command}` : canvasCellKernelId(cell) || "unknown";
    const title = cell.caption || kernel?.title || reference;
    const widgetVersion = version + bump;
    const queryParamSignature = JSON.stringify(projection?.queryParams || {});
    const queryParams = useMemo(
        () => JSON.parse(queryParamSignature),
        [queryParamSignature],
    );
    // `cell` is re-allocated by normalizeRows on every host render, so its
    // `parameters` object is a new reference each time. Key on the content for
    // the same reason as executionPackageSignature above.
    const cellParamSignature = JSON.stringify(cell.parameters || {});
    const effectiveParams = useMemo(() => (
        runtimeCell
            ? {
                ...(kernelParams || {}),
                ...JSON.parse(cellParamSignature),
                ...queryParams,
            }
            : kernelParams
    ), [kernelParams, cellParamSignature, queryParams, runtimeCell]);
    const renderTransform = useCallback(
        (data, spec) => applyCanvasCellProjection(data, spec, projection),
        [projection],
    );
    const dynamicDisclosures = useMemo(() => {
        const disclosures = [...(runtimeCell?.disclosures || [])];
        const ids = new Set(disclosures.map((disclosure) => disclosure.id));
        const dynamicId = (base) => {
            let id = base;
            let suffix = 2;
            while (ids.has(id)) {
                id = `${base}-${suffix}`;
                suffix += 1;
            }
            ids.add(id);
            return id;
        };
        for (const control of projection?.ignoredControls || []) {
            disclosures.push({
                id: dynamicId(`ignored-control-${control.id}`),
                kind: "limitation",
                text: `This view does not respond to the active ${control.label} control.`,
            });
        }
        if (projection?.ignoredFocus) {
            disclosures.push({
                id: dynamicId("ignored-focus"),
                kind: "limitation",
                text: "This view does not apply the active cross-view focus.",
            });
        }
        return disclosures;
    }, [projection?.ignoredControls, projection?.ignoredFocus, runtimeCell?.disclosures]);
    const refresh = useCallback(() => {
        setRefreshState((current) => {
            const nextBump = current.bump + 1;
            return {
                bump: nextBump,
                explicitRunToken: nextBump,
            };
        });
    }, []);
    const runMetadata = useMemo(() => ({
        kind: "canvas",
        cellKey,
        command: kernel?.command || cell.command || null,
    }), [cell.command, cellKey, kernel?.command]);
    const handleRunStateChange = useCallback(
        (event) => onRunStateChange?.(cellKey, event),
        [cellKey, onRunStateChange],
    );
    const handleRunOutcome = useCallback(
        (event) => onRunOutcome?.(cellKey, cell, event),
        [cell, cellKey, onRunOutcome],
    );
    useEffect(() => {
        if (kernel) return undefined;
        let active = true;
        const result = {
            ok: false,
            outcome: KERNEL_RUN_OUTCOME.UNAVAILABLE,
            unavailable: true,
            analyticsQueryCount: 0,
        };
        queueMicrotask(() => {
            if (!active) return;
            handleRunOutcome({
                id: cellKey,
                runId: null,
                outcome: KERNEL_RUN_OUTCOME.UNAVAILABLE,
                runOutcome: KERNEL_RUN_OUTCOME.UNAVAILABLE,
                analyticsQueryCount: 0,
                reason: { code: "kernel-unavailable" },
                result,
                metadata: runMetadata,
            });
        });
        return () => { active = false; };
    }, [cellKey, handleRunOutcome, kernel, runMetadata]);
    return (
        <article
            style={style || undefined}
            className="group relative flex min-w-0 max-w-full flex-col overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[10px]"
        >
            <header className="mb-[9px] pr-[22px]">
                <span
                    className="line-clamp-2 [overflow-wrap:normal] text-[12px] leading-[1.35] text-[color:var(--muted)]"
                    title={title}
                >
                    {title}
                </span>
            </header>
            <HoverTooltip label={reference} placement="bottom">
                <button
                    type="button"
                    onClick={refresh}
                    aria-label={`Refresh ${title}`}
                    data-export-ignore="true"
                    className="absolute right-[10px] top-[10px] flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-[2px] border border-transparent bg-[color:var(--bg)] text-[color:var(--muted-soft)] opacity-0 transition-opacity duration-100 hover:border-[color:var(--line)] hover:text-[color:var(--ink)] group-hover:opacity-100 focus-visible:opacity-100"
                >
                    <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-[11px] w-[11px]"
                        aria-hidden="true"
                    >
                        <path d="M13.4 8a5.4 5.4 0 1 1-1.58-3.82" />
                        <path d="M13.6 2.6v3.2h-3.2" />
                    </svg>
                </button>
            </HoverTooltip>
            <CanvasDisclosures disclosures={dynamicDisclosures} />
            {kernel ? (
                projection?.status && projection.status !== "ready" ? (
                    <CanvasRuntimeNotice status={projection.status} issues={projection.issues} />
                ) : <KernelWidget
                    kernel={kernel}
                    resolvedDefinition={resolvedKernel}
                    runContext={runContext}
                    params={effectiveParams}
                    version={widgetVersion}
                    explicitRunToken={explicitRunToken}
                    explicitRunTrigger={
                        `Refresh canvas cell ${cell.caption || cell.command || kernel?.title || "kernel"}`
                    }
                    runScheduler={runScheduler}
                    executionPackageRequirements={executionPackageRequirements}
                    runKey={cellKey}
                    runTimeoutMs={runTimeoutMs}
                    runMetadata={runMetadata}
                    onRunStateChange={handleRunStateChange}
                    onRunOutcome={handleRunOutcome}
                    onOpenScope={onOpenScope}
                    onFocus={runtimeCell?.focus.length ? (fieldOrFocus, value) => onFocus?.(
                        runtimeCell.id,
                        fieldOrFocus,
                        value,
                    ) : null}
                    renderTransform={projection ? renderTransform : null}
                    showHeader={false}
                    bare
                    fill={false}
                />
            ) : (
                <div className={NOTE_CLASS}>Kernel <code>{reference}</code> unavailable.</div>
            )}
        </article>
    );
}
